import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useAuthContext } from '../../../context/auth.context'
import { lmsAdmin } from '../api'
import { Clip, FAILED_STATE, LmsGrid, byField, exportTime, msOf, useGridRows } from '../lms-grid'
import { useCopy } from '../../../shared/clipboard/clipboard'
import { useGridList } from '../../../shared/sso-workspace/fleet-grid'
import { useIsMobile } from '../../../shared/hooks/use-is-mobile'
import { TableAgClient } from '../../../shared/table-builder/table-ag-client'
import { ColumnFormat } from '../../../shared/table-builder/table-columns'
import { IconActivityLog } from '../../../shared/icon/icon'
import '../lms-admin.scss'
import { LmsIcon } from '../lms-icon'
import { LmsTrail, lmsTrail } from '../lms-trail'

// Activity: the LMS's events in the platform audit trail (read through the LMS
// service's /admin/lms/activity) as the shared AG Grid list, newest first; the
// When column carries the date. Server-side event_type + since filters (the
// two selects above the grid), the grid's own filter box over what is loaded,
// cursor Load more. A bounded LMS
// session's launches (launch.*) read like sessions; the anomalies the LMS
// raises when its signals fire (anomaly.detected, Phase F F-15: one browser
// presenting several learners, one link opened from many browsers) are security
// events, and the anomalies in the range are lifted to a block on top.

const CATEGORY = [
  { prefix: 'session', icon: 'vr-cardboard', className: 'session' },
  { prefix: 'launch', icon: 'vr-cardboard', className: 'session' },
  { prefix: 'anomaly', icon: 'triangle-exclamation', className: 'security' },
  { prefix: 'account_link', icon: 'link', className: 'link' },
  { prefix: 'package', icon: 'box', className: 'build' },
  { prefix: 'course', icon: 'folder', className: 'build' },
  { prefix: 'connection', icon: 'plug', className: 'build' },
  { prefix: 'key', icon: 'key', className: 'security' },
  { prefix: 'pin_claim', icon: 'shield-halved', className: 'security' }
]

const categoryFor = eventType => CATEGORY.find(c => eventType.startsWith(c.prefix)) || { icon: 'circle-info', className: '' }

const ACRONYMS = { lti: 'LTI', lms: 'LMS', scorm: 'SCORM', xapi: 'xAPI', cmi5: 'cmi5' }

const humanize = eventType =>
  eventType
    .replace(/[._]/g, ' ')
    .replace(/^\w/, c => c.toUpperCase())
    .replace(/\b(lti|lms|scorm|xapi|cmi5)\b/gi, word => ACRONYMS[word.toLowerCase()])

// The reasons the LMS service records, in plain words (rapid-api-lms: the
// sweeper, link archive, scoping and connection code). A code not listed is
// humanized (underscores to spaces, sentence case); a free-text reason (a
// person's own words, a refusal's sentence) is shown as written.
const REASONS = {
  ttl_exceeded: 'Timed out',
  connection_archived: 'Connection archived',
  admin_archived: 'Archived by an admin',
  admin_revoked: 'Revoked by an admin',
  user_soft_deleted: 'User deleted',
  new_lms: 'Linked from a new LMS',
  wrong_account_offramp: 'Learner said it was the wrong account'
}
const CODE = /^[a-z0-9]+(_[a-z0-9]+)+$/

export const reasonText = reason => {
  const text = String(reason)
  if (REASONS[text]) return REASONS[text]
  return CODE.test(text) ? humanize(text) : text
}

// The recognizable detail fields as one readable line. The connection, course
// and package have columns of their own, so they are in the line only for
// `all` (the anomaly block, which has no columns).
const detailLine = (details, all = true) => {
  if (!details) return null
  const parts = []
  if (details.lms_user_id) parts.push(`LMS user ${details.lms_user_id}`)
  if (details.edxr_user_id) parts.push(`user #${details.edxr_user_id}`)
  if (all && details.connection_id) parts.push(`connection #${details.connection_id}`)
  if (all && details.node_id) parts.push(`course #${details.node_id}`)
  if (all && details.package_id) parts.push(`package #${details.package_id}`)
  if (details.signal) parts.push(String(details.signal).replace(/_/g, ' '))
  if (details.reason) parts.push(reasonText(details.reason))
  if (details.from && details.to) parts.push(`${details.from} → ${details.to}`)
  return parts.length ? parts.join(' · ') : null
}

const ANOMALY = 'anomaly.detected'

// The feed names an event without the platform's lms. prefix (launch.refused),
// and the service takes either spelling as a filter.
const shortName = eventType => eventType.replace(/^lms\./, '')

// An address may choose the filter before the feed is read: the LMS insights
// pages link to /org/lms/activity?event_type=lms.launch.refused.
const initialEventType = search => new URLSearchParams(search).get('event_type') || ''

const DEFAULT_RANGE = '7d'

const SINCE_OPTIONS = [
  ['24h', 'Last 24 hours', 24 * 3600 * 1000],
  ['7d', 'Last 7 days', 7 * 24 * 3600 * 1000],
  ['30d', 'Last 30 days', 30 * 24 * 3600 * 1000],
  ['all', 'All time', 0]
]

// The session's short ref in mono, with a Copy of the whole ref; the ref opens
// the session (the session's back link returns here, filters and all).
const RefCell = ({ event, onOpen }) => {
  const copy = useCopy()
  const text = useRef(null)
  if (!event.related_session_ref) return <span className="sw-hint">—</span>
  const ref = event.related_session_ref
  return (
    <span className="d-inline-flex align-items-center gap-2">
      <a
        href="#/"
        className="sw-mono"
        title={ref}
        ref={text}
        data-testid={`event-ref-${event.id}`}
        onClick={e => {
          e.preventDefault()
          onOpen(ref)
        }}
      >
        {ref.slice(0, 10)}…
      </a>
      <button
        className="icon-btn"
        type="button"
        title="Copy reference"
        aria-label={`Copy reference ${ref}`}
        data-testid={`event-copy-${event.id}`}
        onClick={() => copy.copy(ref, text)}
      >
        <LmsIcon name={copy.copied ? 'check' : 'copy'} regular={!copy.copied} />
      </button>
    </span>
  )
}

const whenCell = cell =>
  cell.data ? (
    <time dateTime={cell.data.created_at} title={exportTime(cell.data.created_at)}>
      {cell.data.whenText}
    </time>
  ) : ''

const eventCell = cell => {
  const event = cell.data
  if (!event) return ''
  return (
    <span className="d-inline-flex align-items-center gap-2" data-testid={`event-${event.id}`}>
      <span className={`feed-icon ${event.categoryClass}`}>
        <LmsIcon name={event.categoryIcon} />
      </span>
      <Clip size="m" className="fw-semibold">{event.title}</Clip>
    </span>
  )
}

const plain = field => cell => (cell.data && cell.data[field] ? <Clip size="s">{cell.data[field]}</Clip> : <span className="sw-hint">—</span>)

// The When column's CSV value: the precise UTC time.
export const exportWhen = event => exportTime(event.created_at)

const rangeKey = (range, eventType) => `${range}|${eventType}`

export const ViewActivityFeed = () => {
  const history = useHistory()
  const location = useLocation()
  const authContext = useAuthContext()
  const tableRef = useRef()
  const mobile = useIsMobile()

  const [range, updateRange] = useState(DEFAULT_RANGE)
  const [eventType, updateEventType] = useState(() => initialEventType(location.search))
  const [connections, updateConnections] = useState([])

  const sinceISO = key => {
    const option = SINCE_OPTIONS.find(([k]) => k === key)
    if (!option || !option[2]) return undefined
    return new Date(Date.now() - option[2]).toISOString()
  }

  // The events (useGridRows): the grid's ready is the first read, the filter
  // (range and event type) reads again server-side, Load more adds the next page.
  const { rows: events, error, next: nextCursor, current, ready, loadMore } = useGridRows(
    (filter, cursor) => {
      const [chosenRange, chosenType] = filter.split('|')
      return lmsAdmin
        .listActivity({ eventType: chosenType || undefined, since: sinceISO(chosenRange), cursor })
        .then(result => ({ rows: result.events || [], next: result.next_cursor || '' }))
    },
    { filter: rangeKey(range, eventType) }
  )

  useEffect(() => {
    lmsAdmin
      .listConnections()
      .then(result => updateConnections(result.connections || []))
      .catch(() => {})
  }, [])

  const connectionNames = useMemo(() => {
    const map = {}
    connections.forEach(connection => {
      map[connection.id] = connection.name
    })
    return map
  }, [connections])

  // The filter chosen is always one of the options, whether or not an event of
  // that type is among those loaded.
  const eventTypes = useMemo(() => {
    const seen = new Set()
    ;(events || []).forEach(event => seen.add(event.event_type))
    if (eventType) seen.add(shortName(eventType))
    return [...seen].sort()
  }, [events, eventType])

  // The anomalies among the loaded events, unless the list is already only
  // them (the block would say it twice).
  const anomalies = useMemo(
    () => (shortName(eventType) === ANOMALY ? [] : (events || []).filter(event => event.event_type === ANOMALY)),
    [events, eventType]
  )

  // The cells' words, flattened onto the row: the grid sorts, filters and
  // exports the cell VALUE. The event's own id is the row's id.
  const gridRows = useMemo(
    () =>
      Array.isArray(events)
        ? events.map(event => {
          const details = event.details || {}
          const category = categoryFor(event.event_type)
          const title = humanize(event.event_type)
          const detailText = detailLine(details, false) || ''
          const connectionName = details.connection_id ? connectionNames[details.connection_id] || `Connection #${details.connection_id}` : ''
          const courseText = details.node_id ? `#${details.node_id}` : ''
          const packageText = details.package_id ? `#${details.package_id}` : ''
          return {
            ...event,
            title,
            categoryIcon: category.icon,
            categoryClass: category.className,
            whenText: new Date(event.created_at).toLocaleString(),
            whenMs: msOf(event.created_at),
            // the title, the type as stored and everything the event carries, so the filter box finds any of it
            eventText: `${title} ${event.event_type} ${detailText} ${JSON.stringify(details)} ${event.related_session_ref || ''}`,
            connectionName,
            courseText,
            packageText,
            detailText,
            refText: event.related_session_ref || ''
          }
        })
        : events,
    [events, connectionNames]
  )
  const gridList = useGridList(gridRows)

  // a date range other than the default, or an event type, is a filter the empty list can name
  const filtered = Boolean(eventType) || range !== DEFAULT_RANGE
  const customer = authContext.state.organizationName
  const openSession = ref => history.push(`/org/lms/sessions/${ref}`, { from: `${location.pathname}${location.search}` })

  return (
    <div className="lms-admin" data-testid="lms-activity-feed">
      <LmsTrail trail={lmsTrail({ url: '/org/lms/activity', text: 'Activity' })} />
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-header-title">
            Activity
            <span className="page-header-customer">
              <LmsIcon name="building" />
              {customer}
            </span>
          </h1>
          <p className="page-header-sub">
            Everything that's happened across this organization's LMS connections, sessions, builds, and admin actions.
          </p>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {`Couldn't load activity: ${error.message}`}
        </div>
      )}

      <div className="filter-strip filter-strip-alone">
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto' }}
          value={range}
          data-testid="range-filter"
          aria-label="Date range"
          onChange={e => updateRange(e.target.value)}
        >
          {SINCE_OPTIONS.map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto' }}
          value={shortName(eventType)}
          data-testid="event-type-filter"
          aria-label="Event type"
          onChange={e => updateEventType(e.target.value)}
        >
          <option value="">All event types</option>
          {eventTypes.map(type => (
            <option key={type} value={type}>
              {humanize(type)}
            </option>
          ))}
        </select>
        <div className="spacer" />
      </div>

      {anomalies.length > 0 && (
        <div className="lms-card" data-testid="activity-anomalies" style={{ marginBottom: 12 }}>
          <div className="lms-card-head">
            <h3 style={{ margin: 0 }}>
              <LmsIcon name="triangle-exclamation" /> Unusual launch activity
            </h3>
            <span className="feed-detail">
              {anomalies.length} {anomalies.length === 1 ? 'anomaly' : 'anomalies'} in this range
            </span>
            <div className="spacer" />
            <button className="btn btn-light border btn-sm" type="button" onClick={() => updateEventType(ANOMALY)}>
              Show only anomalies
            </button>
          </div>
          <div className="feed-list">
            {anomalies.map(anomaly => {
              const detail = detailLine(anomaly.details)
              return (
                <div className="feed-item" key={anomaly.id} data-testid={`anomaly-${anomaly.id}`}>
                  <div className="feed-icon security">
                    <LmsIcon name="triangle-exclamation" />
                  </div>
                  <div className="feed-body">
                    <div className="feed-title">Unusual launch pattern</div>
                    {detail && <div className="feed-detail">{detail}</div>}
                  </div>
                  <span className="feed-time">{new Date(anomaly.created_at).toLocaleString()}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <LmsGrid
        flush
        title="activity"
        tableRef={tableRef}
        exportName="lms-activity"
        after={
          nextCursor && (
            <React.Fragment>
              <span className="sw-hint">{events ? events.length : 0} events loaded; there are more.</span>
              <button
                className="btn btn-sm sw-btn"
                type="button"
                data-testid="load-more"
                // the cursor is the old filter's while the new one is read
                disabled={!current}
                onClick={loadMore}
              >
                Load more
              </button>
            </React.Fragment>
          )
        }>
        <TableAgClient
          ref={tableRef}
          apiList={gridList}
          onReady={ready}
          sortCol="whenText"
          sortDesc
          emptyState={events === null ? FAILED_STATE : filtered ? {
            Icon: IconActivityLog,
            title: 'No events match this date range or event type',
            message: 'Widen the date range or choose All event types.'
          } : {
            Icon: IconActivityLog,
            title: 'No activity yet',
            message: 'Launches, links, builds, and admin actions will be listed here as your organization uses the LMS connections.'
          }}
          noMatchState={{ message: 'No events match. Clear the search.' }}>
          <ColumnFormat
            field="whenText"
            header="When"
            render={whenCell}
            exportValue={exportWhen}
            comparator={byField('whenMs')}
            minWidth={170}
            sortable
          />
          <ColumnFormat field="eventText" header="Event" render={eventCell} comparator={(a, b, nodeA, nodeB) => String(nodeA?.data?.title || '').localeCompare(String(nodeB?.data?.title || ''))} exportValue={event => event.title} minWidth={220} flex={2} sortable />
          <ColumnFormat field="connectionName" header="Connection" render={plain('connectionName')} minWidth={150} flex={1} sortable hide={mobile} />
          <ColumnFormat field="courseText" header="Course" render={plain('courseText')} minWidth={90} sortable hide={mobile} />
          <ColumnFormat field="packageText" header="Package" render={plain('packageText')} minWidth={90} sortable hide={mobile} />
          <ColumnFormat field="detailText" header="Details" render={plain('detailText')} minWidth={180} flex={2} sortable hide={mobile} />
          <ColumnFormat
            field="refText"
            header="Reference"
            render={cell => (cell.data ? <RefCell event={cell.data} onOpen={openSession} /> : '')}
            exportValue={event => event.related_session_ref || ''}
            minWidth={160}
            sortable
          />
        </TableAgClient>
      </LmsGrid>
    </div>
  )
}
